/**
 * Sur Excel Add-in - Main Taskpane
 * Handles authentication and data upload from Excel to Sur platform
 */

/* global document, Office */

import { storage } from "../utils/storage";
import { api } from "../services/api";
import { excel } from "../services/excel";
import { AppView, UploadPreview, ExcelRowData } from "../types";

// DOM Elements
let elements: {
  loadingView: HTMLElement;
  sideloadMsg: HTMLElement;
  loginView: HTMLElement;
  homeView: HTMLElement;
  uploadView: HTMLElement;
  loginForm: HTMLFormElement;
  emailInput: HTMLInputElement;
  passwordInput: HTMLInputElement;
  loginBtn: HTMLButtonElement;
  loginError: HTMLElement;
  userName: HTMLElement;
  logoutBtn: HTMLButtonElement;
  actionUpload: HTMLElement;
  actionPreview: HTMLElement;
  backBtn: HTMLButtonElement;
  refreshPreview: HTMLButtonElement;
  previewLoading: HTMLElement;
  previewStats: HTMLElement;
  statTotal: HTMLElement;
  statValid: HTMLElement;
  statErrors: HTMLElement;
  previewErrors: HTMLElement;
  errorsList: HTMLElement;
  replaceExisting: HTMLInputElement;
  uploadBtn: HTMLButtonElement;
  uploadResult: HTMLElement;
  resultSuccess: HTMLElement;
  resultError: HTMLElement;
  resultMessage: HTMLElement;
  errorMessage: HTMLElement;
};

// Current state
let currentView: AppView = "login";
let currentPreview: UploadPreview | null = null;

/**
 * Initialize the add-in when Office is ready
 */
Office.onReady((info) => {
  if (info.host === Office.HostType.Excel) {
    initializeElements();
    storage.initialize();
    setupEventListeners();
    checkAuthState();
  } else {
    showView("sideload-msg");
  }
});

/**
 * Initialize DOM element references
 */
function initializeElements(): void {
  elements = {
    loadingView: document.getElementById("loading-view")!,
    sideloadMsg: document.getElementById("sideload-msg")!,
    loginView: document.getElementById("login-view")!,
    homeView: document.getElementById("home-view")!,
    uploadView: document.getElementById("upload-view")!,
    loginForm: document.getElementById("login-form") as HTMLFormElement,
    emailInput: document.getElementById("email") as HTMLInputElement,
    passwordInput: document.getElementById("password") as HTMLInputElement,
    loginBtn: document.getElementById("login-btn") as HTMLButtonElement,
    loginError: document.getElementById("login-error")!,
    userName: document.getElementById("user-name")!,
    logoutBtn: document.getElementById("logout-btn") as HTMLButtonElement,
    actionUpload: document.getElementById("action-upload")!,
    actionPreview: document.getElementById("action-preview")!,
    backBtn: document.getElementById("back-btn") as HTMLButtonElement,
    refreshPreview: document.getElementById("refresh-preview") as HTMLButtonElement,
    previewLoading: document.getElementById("preview-loading")!,
    previewStats: document.getElementById("preview-stats")!,
    statTotal: document.getElementById("stat-total")!,
    statValid: document.getElementById("stat-valid")!,
    statErrors: document.getElementById("stat-errors")!,
    previewErrors: document.getElementById("preview-errors")!,
    errorsList: document.getElementById("errors-list")!,
    replaceExisting: document.getElementById("replace-existing") as HTMLInputElement,
    uploadBtn: document.getElementById("upload-btn") as HTMLButtonElement,
    uploadResult: document.getElementById("upload-result")!,
    resultSuccess: document.getElementById("result-success")!,
    resultError: document.getElementById("result-error")!,
    resultMessage: document.getElementById("result-message")!,
    errorMessage: document.getElementById("error-message")!,
  };
}

/**
 * Setup event listeners
 */
function setupEventListeners(): void {
  // Login form submission
  elements.loginForm.addEventListener("submit", handleLogin);

  // Logout button
  elements.logoutBtn.addEventListener("click", handleLogout);

  // Action cards
  elements.actionUpload.addEventListener("click", () => navigateToUpload());
  elements.actionPreview.addEventListener("click", () => navigateToUpload());

  // Back button
  elements.backBtn.addEventListener("click", () => showView("home-view"));

  // Refresh preview
  elements.refreshPreview.addEventListener("click", () => loadPreview());

  // Upload button
  elements.uploadBtn.addEventListener("click", handleUpload);
}

/**
 * Check if user is already authenticated
 */
async function checkAuthState(): Promise<void> {
  if (storage.isAuthenticated()) {
    const user = storage.getUser();
    if (user) {
      elements.userName.textContent = user.name;
      showView("home-view");
      return;
    }

    // Try to refresh user data from API
    try {
      const user = await api.getCurrentUser();
      storage.setUser(user);
      elements.userName.textContent = user.name;
      showView("home-view");
    } catch {
      // Token invalid, show login
      storage.clearAuth();
      showView("login-view");
    }
  } else {
    showView("login-view");
  }
}

/**
 * Show a specific view and hide others
 */
function showView(viewId: string): void {
  // Hide all views
  elements.loadingView.style.display = "none";
  elements.sideloadMsg.style.display = "none";
  elements.loginView.style.display = "none";
  elements.homeView.style.display = "none";
  elements.uploadView.style.display = "none";

  // Show the requested view
  const view = document.getElementById(viewId);
  if (view) {
    view.style.display = "flex";
  }

  // Update current view state
  if (viewId === "login-view") currentView = "login";
  else if (viewId === "home-view") currentView = "home";
  else if (viewId === "upload-view") currentView = "upload";
}

/**
 * Handle login form submission
 */
async function handleLogin(event: Event): Promise<void> {
  event.preventDefault();

  const email = elements.emailInput.value.trim();
  const password = elements.passwordInput.value;

  if (!email || !password) {
    showLoginError("Please enter your email and password.");
    return;
  }

  // Show loading state
  elements.loginBtn.disabled = true;
  elements.loginBtn.innerHTML = '<span class="ms-Button-label">Signing in...</span>';
  hideLoginError();

  try {
    const response = await api.login({ email, password });
    elements.userName.textContent = response.user.name;
    elements.emailInput.value = "";
    elements.passwordInput.value = "";
    showView("home-view");
  } catch (error) {
    const message = error instanceof Error ? error.message : "Login failed. Please try again.";
    showLoginError(message);
  } finally {
    elements.loginBtn.disabled = false;
    elements.loginBtn.innerHTML = '<span class="ms-Button-label">Sign In</span>';
  }
}

/**
 * Show login error message
 */
function showLoginError(message: string): void {
  elements.loginError.textContent = message;
  elements.loginError.style.display = "block";
}

/**
 * Hide login error message
 */
function hideLoginError(): void {
  elements.loginError.style.display = "none";
}

/**
 * Handle logout
 */
async function handleLogout(): Promise<void> {
  try {
    await api.logout();
  } catch {
    // Ignore logout errors
  }
  showView("login-view");
}

/**
 * Navigate to upload view and load preview
 */
async function navigateToUpload(): Promise<void> {
  showView("upload-view");
  resetUploadState();
  await loadPreview();
}

/**
 * Reset upload view state
 */
function resetUploadState(): void {
  elements.previewStats.style.display = "none";
  elements.previewErrors.style.display = "none";
  elements.uploadResult.style.display = "none";
  elements.resultSuccess.style.display = "none";
  elements.resultError.style.display = "none";
  elements.uploadBtn.disabled = true;
  elements.replaceExisting.checked = false;
  currentPreview = null;
}

/**
 * Load and display preview of worksheet data
 */
async function loadPreview(): Promise<void> {
  elements.previewLoading.style.display = "flex";
  elements.previewStats.style.display = "none";
  elements.previewErrors.style.display = "none";
  elements.uploadBtn.disabled = true;

  try {
    currentPreview = await excel.readWorksheetData();

    // Update stats
    elements.statTotal.textContent = currentPreview.totalRows.toString();
    elements.statValid.textContent = currentPreview.validRows.toString();
    elements.statErrors.textContent = currentPreview.errorRows.toString();
    elements.previewStats.style.display = "flex";

    // Show errors if any
    if (currentPreview.errors.length > 0) {
      elements.errorsList.innerHTML = currentPreview.errors
        .slice(0, 10)
        .map((e) => `<li>Row ${e.row}: ${e.message}</li>`)
        .join("");

      if (currentPreview.errors.length > 10) {
        elements.errorsList.innerHTML += `<li>...and ${currentPreview.errors.length - 10} more errors</li>`;
      }

      elements.previewErrors.style.display = "block";
    }

    // Enable upload if there are valid rows
    elements.uploadBtn.disabled = currentPreview.validRows === 0;
  } catch (error) {
    console.error("Preview error:", error);
    elements.statTotal.textContent = "0";
    elements.statValid.textContent = "0";
    elements.statErrors.textContent = "1";
    elements.previewStats.style.display = "flex";

    elements.errorsList.innerHTML = `<li>${error instanceof Error ? error.message : "Failed to read worksheet data"}</li>`;
    elements.previewErrors.style.display = "block";
  } finally {
    elements.previewLoading.style.display = "none";
  }
}

/**
 * Handle upload to Sur
 */
async function handleUpload(): Promise<void> {
  if (!currentPreview || currentPreview.validRows === 0) {
    return;
  }

  elements.uploadBtn.disabled = true;
  elements.uploadBtn.innerHTML = '<span class="ms-Button-label">Uploading...</span>';
  elements.uploadResult.style.display = "none";
  elements.resultSuccess.style.display = "none";
  elements.resultError.style.display = "none";

  try {
    const mappings = excel.convertToMappingInput(currentPreview.rows);
    const replaceExisting = elements.replaceExisting.checked;

    const result = await api.uploadMappings({
      mappings,
      replaceExisting,
    });

    // Show success
    elements.resultMessage.textContent =
      `Successfully uploaded ${result.created} mappings. ` +
      (result.updated > 0 ? `Updated ${result.updated}. ` : "") +
      (result.skipped > 0 ? `Skipped ${result.skipped}.` : "");
    elements.resultSuccess.style.display = "flex";
    elements.uploadResult.style.display = "block";
  } catch (error) {
    const message = error instanceof Error ? error.message : "Upload failed. Please try again.";
    elements.errorMessage.textContent = message;
    elements.resultError.style.display = "flex";
    elements.uploadResult.style.display = "block";
  } finally {
    elements.uploadBtn.disabled = false;
    elements.uploadBtn.innerHTML = '<span class="ms-Button-label">Upload to Nirvana</span>';
  }
}

// Export for testing
export { handleLogin, handleLogout, loadPreview, handleUpload };
